import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { 
  DollarSign, 
  Percent,
  Download,
  Search,
  Calculator,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  Layers,
  FileSpreadsheet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [markupValue, setMarkupValue] = useState(30);
  const [globalMarkup, setGlobalMarkup] = useState(30);
  const [invoices, setInvoices] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" | "list"

  useEffect(() => {
    fetchProducts();
    fetchInvoices();
  }, []);

  useEffect(() => {
    const filtered = products.filter(p => 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.ean?.includes(search)
    );
    setFilteredProducts(filtered);
  }, [search, products]);

  // Group products by nf_key (invoice)
  const groupedProducts = useMemo(() => {
    const groups = {};
    
    filteredProducts.forEach(product => {
      const nfKey = product.nf_key || "SEM_NOTA";
      if (!groups[nfKey]) {
        // Find invoice info
        const invoice = invoices.find(inv => inv.nf_key === nfKey);
        groups[nfKey] = {
          nfKey,
          supplier: product.supplier || invoice?.supplier || "Fornecedor não identificado",
          emissionDate: product.emission_date || invoice?.emission_date || null,
          products: [],
          totalCost: 0,
          withPrice: 0,
          withoutPrice: 0
        };
      }
      groups[nfKey].products.push(product);
      groups[nfKey].totalCost += (product.cost_price || 0) * (product.quantity || 0);
      if (product.sale_price > 0) {
        groups[nfKey].withPrice++;
      } else {
        groups[nfKey].withoutPrice++;
      }
    });

    // Sort groups by emission date (most recent first)
    return Object.values(groups).sort((a, b) => {
      if (!a.emissionDate && !b.emissionDate) return 0;
      if (!a.emissionDate) return 1;
      if (!b.emissionDate) return -1;
      return new Date(b.emissionDate) - new Date(a.emissionDate);
    });
  }, [filteredProducts, invoices]);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`);
      setInvoices(res.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
      setFilteredProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === filteredProducts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredProducts.map(p => p.id));
    }
  };

  // Toggle select all products in a specific group (invoice)
  const toggleSelectGroup = (nfKey) => {
    const group = groupedProducts.find(g => g.nfKey === nfKey);
    if (!group) return;
    
    const groupProductIds = group.products.map(p => p.id);
    const allSelected = groupProductIds.every(id => selectedIds.includes(id));
    
    if (allSelected) {
      // Deselect all in this group
      setSelectedIds(prev => prev.filter(id => !groupProductIds.includes(id)));
    } else {
      // Select all in this group
      setSelectedIds(prev => [...new Set([...prev, ...groupProductIds])]);
    }
  };

  // Check if all products in a group are selected
  const isGroupSelected = (nfKey) => {
    const group = groupedProducts.find(g => g.nfKey === nfKey);
    if (!group || group.products.length === 0) return false;
    return group.products.every(p => selectedIds.includes(p.id));
  };

  // Check if some products in a group are selected
  const isGroupPartiallySelected = (nfKey) => {
    const group = groupedProducts.find(g => g.nfKey === nfKey);
    if (!group || group.products.length === 0) return false;
    const selectedCount = group.products.filter(p => selectedIds.includes(p.id)).length;
    return selectedCount > 0 && selectedCount < group.products.length;
  };

  // Toggle expand/collapse a group
  const toggleGroup = (nfKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [nfKey]: !prev[nfKey]
    }));
  };

  // Expand all groups
  const expandAllGroups = () => {
    const allExpanded = {};
    groupedProducts.forEach(g => {
      allExpanded[g.nfKey] = true;
    });
    setExpandedGroups(allExpanded);
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    setExpandedGroups({});
  };

  const applyMarkupSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione ao menos um produto");
      return;
    }

    try {
      const res = await axios.post(`${API}/pricing/apply-markup`, {
        product_ids: selectedIds,
        markup_percentage: markupValue
      });
      toast.success(res.data.message);
      fetchProducts();
      setSelectedIds([]);
    } catch (error) {
      toast.error("Erro ao aplicar markup");
    }
  };

  const applyMarkupAll = async () => {
    try {
      const res = await axios.post(`${API}/pricing/apply-markup-all`, {
        markup_percentage: globalMarkup
      });
      toast.success(res.data.message);
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao aplicar markup");
    }
  };

  const roundPricesSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione ao menos um produto");
      return;
    }

    try {
      const res = await axios.post(`${API}/pricing/round-prices`, {
        product_ids: selectedIds
      });
      toast.success(res.data.message);
      fetchProducts();
      setSelectedIds([]);
    } catch (error) {
      toast.error("Erro ao arredondar preços");
    }
  };

  const roundAllPrices = async () => {
    try {
      const res = await axios.post(`${API}/pricing/round-all`);
      toast.success(res.data.message);
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao arredondar preços");
    }
  };

  const exportOnii = async (format = 'xlsx') => {
    try {
      const endpoint = format === 'xlsx' ? '/pricing/export-onii-xlsx' : '/pricing/export-onii';
      const res = await axios.get(`${API}${endpoint}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `onii_precos.${format === 'xlsx' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Arquivo exportado!", {
        description: `Preços exportados no formato Onii (.${format})`
      });
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calculate preview price
  const previewPrice = (costPrice, markup) => {
    return costPrice * (1 + markup / 100);
  };

  // Round to x.x9
  const roundToX9 = (value) => {
    const intPart = Math.floor(value);
    const decPart = value - intPart;
    const firstDecimal = Math.floor(decPart * 10);
    return intPart + (firstDecimal / 10) + 0.09;
  };

  const productsWithPrice = products.filter(p => p.sale_price > 0).length;

  return (
    <div className="p-8 animate-fadeIn" data-testid="pricing-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Precificação</h1>
        <p className="text-muted-foreground">Defina markup e exporte preços para Onii</p>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Global Markup Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Markup Global
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              <Input
                type="number"
                value={globalMarkup}
                onChange={(e) => setGlobalMarkup(parseFloat(e.target.value) || 0)}
                className="w-24 font-mono"
                data-testid="global-markup-input"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <Button 
              onClick={applyMarkupAll} 
              className="w-full"
              data-testid="apply-markup-all-btn"
            >
              Aplicar a Todos
            </Button>
          </CardContent>
        </Card>

        {/* Round Prices Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Arredondamento x,x9
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Ex: R$ 21,03 → R$ 21,09
            </p>
            <Button 
              onClick={roundAllPrices} 
              variant="secondary" 
              className="w-full"
              data-testid="round-all-btn"
            >
              Arredondar Todos
            </Button>
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="w-4 h-4" />
              Exportar Onii
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              {productsWithPrice} produtos com preço
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={() => exportOnii('xlsx')} 
                variant="outline" 
                className="flex-1"
                data-testid="export-xlsx-btn"
              >
                Excel
              </Button>
              <Button 
                onClick={() => exportOnii('csv')} 
                variant="outline" 
                className="flex-1"
                data-testid="export-csv-btn"
              >
                CSV
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="mb-6 bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="font-medium">{selectedIds.length} produtos selecionados</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={markupValue}
                    onChange={(e) => setMarkupValue(parseFloat(e.target.value) || 0)}
                    className="w-20 font-mono"
                    data-testid="selected-markup-input"
                  />
                  <span>%</span>
                  <Button size="sm" onClick={applyMarkupSelected} data-testid="apply-selected-btn">
                    Aplicar Markup
                  </Button>
                </div>
                <Button size="sm" variant="outline" onClick={roundPricesSelected} data-testid="round-selected-btn">
                  Arredondar x,x9
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Card with View Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" strokeWidth={1.5} />
              Produtos para Precificação
              {viewMode === "grouped" && (
                <Badge variant="outline" className="ml-2 font-normal">
                  <FileText className="w-3 h-3 mr-1" />
                  {groupedProducts.length} notas
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex items-center border rounded-md">
                <Button
                  variant={viewMode === "grouped" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grouped")}
                  className="rounded-r-none"
                  data-testid="view-grouped-btn"
                >
                  <Layers className="w-4 h-4 mr-1" />
                  Por Nota
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-l-none"
                  data-testid="view-list-btn"
                >
                  <Package className="w-4 h-4 mr-1" />
                  Lista
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar..."
                  className="pl-10"
                  data-testid="pricing-search"
                />
              </div>
            </div>
          </div>
          
          {/* Expand/Collapse All (only in grouped view) */}
          {viewMode === "grouped" && groupedProducts.length > 0 && (
            <div className="flex gap-2 mt-3">
              <Button variant="ghost" size="sm" onClick={expandAllGroups}>
                Expandir Todos
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAllGroups}>
                Recolher Todos
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum produto encontrado</p>
              <p className="text-sm">Importe uma nota fiscal para adicionar produtos</p>
            </div>
          ) : viewMode === "grouped" ? (
            /* Grouped View - Products by Invoice */
            <div className="space-y-4 max-h-[600px] overflow-y-auto" data-testid="grouped-view">
              {groupedProducts.map((group) => {
                const isExpanded = expandedGroups[group.nfKey] ?? false;
                const groupSelected = isGroupSelected(group.nfKey);
                const groupPartial = isGroupPartiallySelected(group.nfKey);
                const formatDate = (dateStr) => {
                  if (!dateStr) return "";
                  try {
                    const date = new Date(dateStr);
                    return date.toLocaleDateString('pt-BR');
                  } catch {
                    return "";
                  }
                };
                
                return (
                  <Collapsible 
                    key={group.nfKey} 
                    open={isExpanded}
                    onOpenChange={() => toggleGroup(group.nfKey)}
                  >
                    {/* Group Header */}
                    <div className="border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3 p-3">
                        {/* Checkbox for Group */}
                        <Checkbox 
                          checked={groupSelected}
                          ref={(el) => {
                            if (el && groupPartial && !groupSelected) {
                              el.dataset.state = "indeterminate";
                            }
                          }}
                          onCheckedChange={() => toggleSelectGroup(group.nfKey)}
                          data-testid={`group-checkbox-${group.nfKey.slice(-8)}`}
                        />
                        
                        {/* Expand/Collapse Trigger */}
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="p-0 h-auto hover:bg-transparent">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        
                        {/* Group Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate" title={group.supplier}>
                              {group.supplier}
                            </span>
                            {group.emissionDate && (
                              <Badge variant="outline" className="text-xs">
                                {formatDate(group.emissionDate)}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                            <span>{group.products.length} produtos</span>
                            <span className="text-green-600">{group.withPrice} precificados</span>
                            {group.withoutPrice > 0 && (
                              <span className="text-orange-600">{group.withoutPrice} sem preço</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick Actions for Group */}
                        <div className="flex items-center gap-2">
                          {/* Export Button for this Invoice */}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs gap-1"
                            disabled={group.withPrice === 0}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (group.withPrice === 0) {
                                toast.error("Nenhum produto precificado nesta nota");
                                return;
                              }
                              try {
                                const res = await axios.post(`${API}/pricing/export-onii-by-invoice`, {
                                  nf_key: group.nfKey
                                }, { responseType: 'blob' });
                                
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                const shortKey = group.nfKey.slice(-8);
                                link.setAttribute('download', `onii_precos_NF_${shortKey}.xlsx`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                                
                                toast.success(`Excel exportado!`, {
                                  description: `${group.withPrice} produtos da nota ${shortKey}`
                                });
                              } catch (error) {
                                toast.error("Erro ao exportar Excel");
                              }
                            }}
                            data-testid={`export-group-${group.nfKey.slice(-8)}`}
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                            Excel
                          </Button>
                          
                          {/* Markup Input and Apply */}
                          <div className="flex items-center gap-1 bg-background rounded px-2 py-1">
                            <Input
                              type="number"
                              defaultValue={30}
                              className="w-16 h-7 text-xs font-mono"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`group-markup-${group.nfKey.slice(-8)}`}
                              id={`markup-${group.nfKey}`}
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                            <Button 
                              size="sm" 
                              variant="secondary"
                              className="h-7 text-xs"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const input = document.getElementById(`markup-${group.nfKey}`);
                                const markup = parseFloat(input?.value) || 30;
                                const productIds = group.products.map(p => p.id);
                                try {
                                  const res = await axios.post(`${API}/pricing/apply-markup`, {
                                    product_ids: productIds,
                                    markup_percentage: markup
                                  });
                                  toast.success(`Markup aplicado a ${productIds.length} produtos`);
                                  fetchProducts();
                                } catch (error) {
                                  toast.error("Erro ao aplicar markup");
                                }
                              }}
                              data-testid={`apply-group-markup-${group.nfKey.slice(-8)}`}
                            >
                              Aplicar
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Content - Product List */}
                      <CollapsibleContent>
                        <div className="border-t">
                          <table className="data-table w-full" data-testid={`group-table-${group.nfKey.slice(-8)}`}>
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="w-10"></th>
                                <th className="text-xs">EAN</th>
                                <th className="text-xs">Produto</th>
                                <th className="text-xs">Custo</th>
                                <th className="text-xs">Markup</th>
                                <th className="text-xs">Preço</th>
                                <th className="text-xs">Preview</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.products.map((product) => {
                                const preview = product.sale_price > 0 ? roundToX9(product.sale_price) : 0;
                                
                                return (
                                  <tr key={product.id} className="hover:bg-muted/30">
                                    <td>
                                      <Checkbox 
                                        checked={selectedIds.includes(product.id)}
                                        onCheckedChange={() => toggleSelect(product.id)}
                                        data-testid={`checkbox-${product.id}`}
                                      />
                                    </td>
                                    <td className="font-mono text-xs">{product.ean}</td>
                                    <td className="max-w-[180px] truncate text-sm" title={product.name}>
                                      {product.name}
                                    </td>
                                    <td className="font-mono text-sm">{formatCurrency(product.cost_price)}</td>
                                    <td className="font-mono text-sm">
                                      {product.markup ? (
                                        <Badge variant="secondary" className="font-mono text-xs">
                                          {product.markup.toFixed(1)}%
                                        </Badge>
                                      ) : '-'}
                                    </td>
                                    <td className="font-mono text-sm font-medium">
                                      {product.sale_price > 0 ? formatCurrency(product.sale_price) : (
                                        <span className="text-orange-500">-</span>
                                      )}
                                    </td>
                                    <td className="font-mono text-sm text-primary font-medium">
                                      {preview > 0 ? formatCurrency(preview) : '-'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            /* List View - All Products */
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="data-table" data-testid="pricing-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th className="w-10">
                      <Checkbox 
                        checked={selectedIds.length === filteredProducts.length && filteredProducts.length > 0}
                        onCheckedChange={selectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th>EAN</th>
                    <th>Produto</th>
                    <th>Custo</th>
                    <th>Markup</th>
                    <th>Preço Atual</th>
                    <th>Preview x,x9</th>
                    <th>Centavos</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const preview = product.sale_price > 0 ? roundToX9(product.sale_price) : 0;
                    const cents = Math.round(preview * 100);
                    
                    return (
                      <tr key={product.id}>
                        <td>
                          <Checkbox 
                            checked={selectedIds.includes(product.id)}
                            onCheckedChange={() => toggleSelect(product.id)}
                            data-testid={`checkbox-${product.id}`}
                          />
                        </td>
                        <td className="font-mono text-xs">{product.ean}</td>
                        <td className="max-w-[200px] truncate" title={product.name}>
                          {product.name}
                        </td>
                        <td className="font-mono">{formatCurrency(product.cost_price)}</td>
                        <td className="font-mono">
                          {product.markup ? (
                            <Badge variant="secondary" className="font-mono">
                              {product.markup.toFixed(1)}%
                            </Badge>
                          ) : '-'}
                        </td>
                        <td className="font-mono font-medium">
                          {product.sale_price > 0 ? formatCurrency(product.sale_price) : '-'}
                        </td>
                        <td className="font-mono text-primary font-medium">
                          {preview > 0 ? formatCurrency(preview) : '-'}
                        </td>
                        <td className="font-mono text-xs text-muted-foreground">
                          {cents > 0 ? cents : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Format Info */}
      <Card className="mt-6">
        <CardContent className="py-4">
          <h3 className="font-medium mb-2">Formato de Exportação Onii</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• <strong>Coluna EAN:</strong> Código de barras do produto (texto)</p>
            <p>• <strong>Coluna Preço:</strong> Valor em centavos, sem vírgula (ex: R$ 8,35 → 835)</p>
            <p>• Os preços são automaticamente arredondados para x,x9 antes da exportação</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Pricing;
